import { useEffect, useState, useRef } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import axios from "axios";
import MagneticButton from "@/components/MagneticButton";
import Tilt3D from "@/components/Tilt3D";
import LoadingScreen from "@/components/LoadingScreen";
import CursorTracker from "@/components/CursorTracker";
import RunwayDivider from "@/components/RunwayDivider";
import TakeoffCTA from "@/components/TakeoffCTA";
import LiveActivityToast from "@/components/LiveActivityToast";
import PilotCarIcon from "@/components/PilotCarIcon";
import { TurkishLanding, EnglishLanding } from "@/pages/Languages";
import SpoedPage from "@/pages/Spoed";
import TarievenPage from "@/pages/Tarieven";
import LeskaartPage from "@/pages/Leskaart";
import { BlogIndex, BlogDetail } from "@/pages/Blog";
import {
  WhatsappLogo,
  InstagramLogo,
  TiktokLogo,
  ArrowRight,
  CheckCircle,
  Star,
  ShieldCheck,
  Clock,
  GraduationCap,
  Gauge,
  Compass,
  List,
  X,
  CaretDown,
  Question,
} from "@phosphor-icons/react";
import ChatWidget from "@/components/ChatWidget";
import PaymentSuccess from "@/components/PaymentSuccess";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const WHATSAPP_NUMBER = "31620933773"; // +31 6 20933773
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/6ecc2oy7_20251118_232018_0000.jpg";

// ----------------- reCAPTCHA v3 (invisible) -----------------
const getCaptchaToken = async (action = "submit") => {
  if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return null;
  try {
    await new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(resolve);
      } else {
        resolve();
      }
    });
    return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  } catch (e) {
    console.error("reCAPTCHA execute failed:", e);
    return null;
  }
};

// Owner photo (Samet voor de SEAT Leon)
const OWNER_PHOTO =
  "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/jxnhsb0j_Photographic%20White%20Frame%20Reminder%20Instagram%20Story_20250528_155544_0000.png";

// Student testimonials (reviews)
const TESTIMONIALS = [
  {
    id: "t1",
    image:
      "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/kvkkcz80_Photographic%20White%20Frame%20Reminder%20Instagram%20Story_20250225_180011_0000.png",
    label: "AUTOPILOT #1",
    quote: "Verkeer, maak je klaar… ik ben officieel #AUTOPILOT!",
  },
  {
    id: "t2",
    image:
      "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/ipdh9lux_Photographic%20White%20Frame%20Reminder%20Instagram%20Story_20250401_164119_0000.png",
    label: "AUTOPILOT #2",
    quote:
      "Sommigen trappen op 1 april in een grap, jij trapt op de koppeling. Geslaagd en klaar voor de weg!",
  },
  {
    id: "t3",
    image:
      "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/vo4loplo_Photographic%20White%20Frame%20Reminder%20Instagram%20Story_20250512_103038_0000.png",
    label: "AUTOPILOT #3",
    quote:
      "Scalpel in de ene hand, stuur in de andere — deze toekomstig chirurg is geslaagd! Scherpe bochten en rechte lijnen.",
  },
  {
    id: "t4",
    image:
      "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/jidjxqzk_Photographic%20White%20Frame%20Reminder%20Instagram%20Story_20250519_100222_0000.png",
    label: "AUTOPILOT #4",
    quote:
      "Missie geslaagd! Topper van de geneeskundige dienst van Defensie — nu ook veilig door het verkeer.",
  },
  {
    id: "t5",
    image:
      "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/jxnhsb0j_Photographic%20White%20Frame%20Reminder%20Instagram%20Story_20250528_155544_0000.png",
    label: "AUTOPILOT #5",
    quote:
      "Toen ik slaagde zat hij naast me als instructeur. Nu zat ik achterin als trotse instructeur. In één keer geslaagd!",
  },
];

// Seasonal hero images (AI generated black SEAT Leon with AutoPilot lesbord)
const SEASONS = [
  { id: "summer", label: "Zomer", src: "/seasons/summer.png" },
  { id: "autumn", label: "Herfst", src: "/seasons/autumn.png" },
  { id: "winter", label: "Winter", src: "/seasons/winter.png" },
  { id: "spring", label: "Lente", src: "/seasons/spring.png" },
];

const whatsappLink = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

// ----------------- SCROLL TO TOP ON ROUTE CHANGE -----------------
const PAGE_META = {
  "/": {
    title: "Rijschool AutoPilot | Rijles Eindhoven & Helmond — Drive like a pilot",
    description: "Premium rijles in Eindhoven en Helmond. Gratis proefles, ervaren instructeurs, hoog slagingspercentage. Pakketten vanaf €1.800.",
  },
  "/waarom": {
    title: "Waarom Rijschool AutoPilot? | 5 redenen om voor ons te kiezen",
    description: "Persoonlijke begeleiding, structuur, ervaren instructeurs en gratis ophaalservice in Eindhoven en Helmond. Ontdek waarom leerlingen voor AutoPilot kiezen.",
  },
  "/pakketten": {
    title: "Rijles Pakketten | Economy €1.800 · Business €2.400 · First Class €3.000",
    description: "Drie rijles pakketten met gratis proefles, examen en 1 herexamen inbegrepen. Kies wat bij jou past — Economy, Business of First Class.",
  },
  "/reviews": {
    title: "Reviews | Geslaagde leerlingen Rijschool AutoPilot Eindhoven & Helmond",
    description: "Lees ervaringen van onze geslaagde leerlingen. Hoog slagingspercentage en tevreden bestuurders bij Rijschool AutoPilot.",
  },
  "/over-ons": {
    title: "Over ons | Samet Dersuneli — Rijschool AutoPilot Eindhoven & Helmond",
    description: "Maak kennis met Samet en Rijschool AutoPilot. Persoonlijke rijbegeleiding op een ontspannen, professionele manier.",
  },
  "/faq": {
    title: "Veelgestelde vragen | Rijschool AutoPilot Eindhoven & Helmond",
    description: "Antwoorden op de meest gestelde vragen over rijles, pakketten, examen en proefles bij Rijschool AutoPilot.",
  },
  "/contact": {
    title: "Contact & Gratis Proefles | Rijschool AutoPilot Eindhoven & Helmond",
    description: "Plan je gratis proefles bij Rijschool AutoPilot. WhatsApp, contactformulier of social media — wij helpen je graag.",
  },
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const meta = PAGE_META[pathname] || PAGE_META["/"];
    document.title = meta.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", meta.description);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute(
      "href",
      `https://www.rijschool-autopilot.nl${pathname === "/" ? "/" : pathname}`
    );
  }, [pathname]);
  return null;
};

// ----------------- NAVBAR -----------------
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/waarom", label: "Waarom wij" },
    { to: "/pakketten", label: "Pakketten" },
    { to: "/tarieven", label: "Tarieven" },
    { to: "/spoed", label: "Spoed" },
    { to: "/leskaart", label: "Leskaart" },
    { to: "/blog", label: "Blog" },
    { to: "/reviews", label: "Reviews" },
    { to: "/over-ons", label: "Over ons" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm transition-colors tracking-wide ${
      isActive ? "text-gold" : "text-silver hover:text-gold"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "glass" : ""
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="nav-logo-link"
        >
          <img
            src={LOGO_URL}
            alt="Rijschool AutoPilot"
            className="h-12 w-12 object-cover rounded-full border border-[rgba(212,175,55,0.4)]"
          />
          <div className="hidden sm:block leading-tight">
            <div className="text-white text-sm font-semibold tracking-wide">
              Rijschool AutoPilot
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
              Drive like a pilot
            </div>
          </div>
        </Link>

        <ul className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end
                className={linkClass}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Language switcher (desktop) */}
        <div className="hidden md:flex items-center gap-2 mr-2">
          <NavLink
            to="/tr"
            end
            className={({ isActive }) =>
              `lang-pill ${isActive ? "lang-pill-active" : ""}`
            }
            data-testid="nav-lang-tr"
            aria-label="Türkçe"
            title="Türkçe"
          >
            TR
          </NavLink>
          <NavLink
            to="/en"
            end
            className={({ isActive }) =>
              `lang-pill ${isActive ? "lang-pill-active" : ""}`
            }
            data-testid="nav-lang-en"
            aria-label="English"
            title="English"
          >
            EN
          </NavLink>
        </div>

        <Link
          to="/contact"
          className="hidden md:inline-flex btn-gold text-sm"
          data-testid="nav-cta-btn"
        >
          Gratis Proefles <ArrowRight size={16} weight="bold" className="anim-arrow" />
        </Link>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/10"
          >
            <ul className="flex flex-col px-6 py-4 gap-4">
              {links.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 ${
                        isActive ? "text-gold" : "text-silver hover:text-gold"
                      }`
                    }
                    data-testid={`mobile-nav-link-${l.to.replace("/", "")}`}
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-gold text-sm justify-center"
                data-testid="mobile-nav-cta"
              >
                Gratis Proefles
              </Link>

              {/* Mobile language switcher */}
              <li className="flex items-center gap-3 pt-2 border-t border-white/10 mt-2">
                <span className="text-xs uppercase tracking-[0.2em] text-silver/60">Taal</span>
                <NavLink
                  to="/tr"
                  end
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `lang-pill ${isActive ? "lang-pill-active" : ""}`
                  }
                  data-testid="mobile-nav-lang-tr"
                >
                  TR
                </NavLink>
                <NavLink
                  to="/en"
                  end
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `lang-pill ${isActive ? "lang-pill-active" : ""}`
                  }
                  data-testid="mobile-nav-lang-en"
                >
                  EN
                </NavLink>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// ----------------- HERO -----------------
const Hero = () => {
  const [seasonIdx, setSeasonIdx] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  useEffect(() => {
    const id = setInterval(() => {
      setSeasonIdx((i) => (i + 1) % SEASONS.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const season = SEASONS[seasonIdx];

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center pt-24 overflow-hidden noise"
      data-testid="hero-section"
    >
      {/* Parallax seasonal background */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: bgY, scale: bgScale }}
      >
        <AnimatePresence mode="sync">
          <motion.img
            key={season.id}
            src={season.src}
            alt={`Rijschool AutoPilot — ${season.label}`}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.55, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2b]/75 via-[#1a1f2b]/55 to-[#1a1f2b]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1f2b] via-[#1a1f2b]/30 to-transparent"></div>
      </motion.div>

      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center w-full py-24"
        style={{ y: fgY }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="runway-stripes w-24 block"></span>
            <span className="overline">Rijschool Eindhoven & Helmond</span>
          </div>

          <h1
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light leading-[0.95] mb-6"
            data-testid="hero-title"
          >
            Drive like <br />
            <span className="font-display-italic text-shimmer text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] leading-[0.9]">a pilot.</span>
          </h1>

          <p className="text-lg md:text-xl text-silver max-w-xl mb-10 leading-relaxed">
            Persoonlijke rijlessen met structuur, rust en een snufje luxe.
            Stap zelfverzekerd de weg op — begeleid door ervaren instructeurs,
            afgestemd op jouw tempo.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                className="btn-gold"
                data-testid="hero-primary-cta"
              >
                Plan gratis proefles <ArrowRight size={18} weight="bold" className="anim-arrow" />
              </Link>
            </MagneticButton>
            <MagneticButton>
              <Link
                to="/pakketten"
                className="btn-ghost"
                data-testid="hero-secondary-cta"
              >
                Bekijk pakketten
              </Link>
            </MagneticButton>
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-8 text-sm text-silver">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} weight="duotone" className="text-gold anim-check" />
              Gratis proefles
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} weight="duotone" className="text-gold anim-check" style={{animationDelay:'0.15s'}} />
              1 gratis herexamen
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} weight="duotone" className="text-gold anim-check" style={{animationDelay:'0.3s'}} />
              Persoonlijke aanpak
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="lg:col-span-5 hidden lg:block"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[rgba(212,175,55,0.25)] to-transparent blur-3xl"></div>
            <div className="relative card-highlight scan-line p-10 flex flex-col gap-6 overflow-hidden" data-testid="hero-flight-log">
              <div className="flex items-center justify-between">
                <span className="overline">Flight Log</span>
                <Compass size={22} className="text-gold anim-compass" weight="duotone" />
              </div>
              <img
                src={LOGO_URL}
                alt="Logo"
                className="w-40 h-40 mx-auto rounded-full object-cover"
              />
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs text-silver">
                  <span className="tracking-wider uppercase">Seizoen</span>
                  <span className="text-gold text-sm font-semibold">
                    {season.label}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {SEASONS.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => setSeasonIdx(i)}
                      className={`flex-1 h-1 transition-all ${
                        i === seasonIdx ? "bg-gold" : "bg-white/15"
                      }`}
                      aria-label={`Toon ${s.label}`}
                      data-testid={`hero-season-btn-${s.id}`}
                    />
                  ))}
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <div className="flex justify-between text-xs text-silver">
                  <span>STATUS</span>
                  <span className="text-gold flex items-center gap-2">
                    <span className="w-2 h-2 bg-gold rounded-full pulse-dot"></span>
                    Cleared for takeoff
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Runway lights along the bottom edge of the hero */}
      <div className="hero-runway" aria-hidden="true" data-testid="hero-runway-lights" />
    </section>
  );
};

// ----------------- FEATURES BENTO -----------------
const Features = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.1 },
    }),
  };

  return (
    <section
      id="waarom"
      className="relative py-24 lg:py-32 px-6 md:px-12"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16 max-w-2xl"
        >
          <div className="overline mb-4">Waarom AutoPilot</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
            Rij slim. Rij veilig.
            <br />
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
              Rij op Autopilot.
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Big tile */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
            variants={fadeUp}
            className="card md:col-span-7 p-8 lg:p-12 relative overflow-hidden"
            data-testid="feature-tile-structuur"
          >
            <div className="flex items-start justify-between mb-6">
              <GraduationCap size={40} className="text-gold anim-cap" weight="duotone" />
              <span className="overline">01</span>
            </div>
            <h3 className="text-2xl md:text-3xl text-white tracking-tight mb-3">
              Lessen met structuur
            </h3>
            <p className="text-silver leading-relaxed max-w-lg">
              Elke les bouwt logisch voort op de vorige. Je leert stap voor
              stap — van bijzondere verrichtingen tot snelwegrijden — met een
              duidelijk lesplan waar je zelfvertrouwen van krijgt.
            </p>
            <div className="runway-stripes mt-10 w-32"></div>
          </motion.div>

          {/* Small tile */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="card md:col-span-5 p-8 lg:p-10"
            data-testid="feature-tile-instructeurs"
          >
            <ShieldCheck size={36} className="text-gold mb-5 anim-shield" weight="duotone" />
            <h3 className="text-xl md:text-2xl text-white tracking-tight mb-3">
              Ervaren instructeurs
            </h3>
            <p className="text-silver leading-relaxed">
              Rustig, duidelijk en geduldig. Iedereen leert anders — wij
              passen ons aan jou aan, niet andersom.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeUp}
            className="card md:col-span-4 p-8 lg:p-10"
            data-testid="feature-tile-acties"
          >
            <Star size={36} className="text-gold mb-5" weight="duotone" />
            <h3 className="text-xl md:text-2xl text-white tracking-tight mb-3">
              Leuke acties & kortingen
            </h3>
            <p className="text-silver leading-relaxed">
              Profiteer van seizoensacties en pakketkortingen. Meer lessen,
              minder kosten.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={3}
            variants={fadeUp}
            className="card md:col-span-4 p-8 lg:p-10"
            data-testid="feature-tile-examen"
          >
            <Gauge size={36} className="text-gold mb-5 anim-gauge" weight="duotone" />
            <h3 className="text-xl md:text-2xl text-white tracking-tight mb-3">
              Examentraining op maat
            </h3>
            <p className="text-silver leading-relaxed">
              Geen standaard herhaling. Wij pakken gericht aan wat jij lastig
              vindt: faalangst, snelwegen of verkeersinzicht.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={4}
            variants={fadeUp}
            className="card md:col-span-4 p-8 lg:p-10 relative overflow-hidden"
            data-testid="feature-tile-regio"
          >
            <Clock size={36} className="text-gold mb-5 anim-clock" weight="duotone" />
            <h3 className="text-xl md:text-2xl text-white tracking-tight mb-3">
              Eindhoven & Helmond
            </h3>
            <p className="text-silver leading-relaxed">
              Flexibele tijden, ophaalservice in de regio. Zo past rijles
              makkelijk in je week.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ----------------- COUNT UP (scroll-triggered) -----------------
const CountUp = ({ to, duration = 1400, prefix = "€", suffix = "", className = "" }) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (started) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(to * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, to, duration]);

  const formatted = value.toLocaleString("nl-NL");
  return (
    <span ref={ref} className={className} aria-label={`${prefix}${to.toLocaleString("nl-NL")}${suffix}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

// ----------------- FAQ -----------------
const FAQ_ITEMS = [
  {
    q: "Hoeveel rijlessen heb ik gemiddeld nodig?",
    a: "Dat verschilt per persoon. Gemiddeld zit dat tussen de 30 en 45 lessen, maar sommige leerlingen halen het sneller en anderen hebben er meer nodig. Tijdens de gratis proefles geven we je een eerlijke inschatting van wat jij nodig hebt.",
  },
  {
    q: "Wat kost een proefles?",
    a: "De eerste proefles is helemaal gratis — geen verplichtingen achteraf. Zo kun je rustig kennismaken met de instructeur en de auto.",
  },
  {
    q: "Vanaf welke leeftijd kan ik beginnen met rijlessen?",
    a: "Je kunt vanaf 16,5 jaar starten met rijlessen (categorie B). Je examen mag je doen vanaf 17 jaar via het 2toDrive programma, of vanaf 18 jaar volgens de gewone weg.",
  },
  {
    q: "Hoe lang duurt een rijles?",
    a: "Bij AutoPilot is een rijles 60 minuten effectief rijden. We werken niet met losse 45-minuten-blokken, omdat 60 minuten beter rendement geeft.",
  },
  {
    q: "Word ik thuis opgehaald?",
    a: "Ja, in Eindhoven en Helmond halen we je gratis op vanaf elk adres binnen onze regio. Vanaf het moment dat we vertrekken begint je les — geen verloren tijd.",
  },
  {
    q: "Wat gebeurt er als ik zak voor mijn examen?",
    a: "Bij elk pakket zit 1 gratis herexamen inbegrepen. We bekijken samen wat er fout ging, geven gerichte training, en gaan opnieuw met vol vertrouwen het examen in.",
  },
  {
    q: "Heb ik een theoriecertificaat nodig?",
    a: "Voordat je praktijkexamen mag doen moet je geslaagd zijn voor je theorie-examen bij het CBR. We raden aan om dit binnen de eerste maanden van je rijlessen te halen.",
  },
  {
    q: "Kan ik tussentijds stoppen of pauzeren?",
    a: "Ja, je betaalt per pakket en we plannen de lessen flexibel in. Mocht het door omstandigheden niet uitkomen, dan is overleg altijd mogelijk.",
  },
  {
    q: "Hoe betaal ik mijn rijlessen?",
    a: "Betaling gaat per overschrijving. Na je proefles bespreken we het pakket en ontvang je de gegevens. Je kunt eventueel ook in termijnen betalen — vraag ernaar.",
  },
  {
    q: "Wat is jullie slagingspercentage?",
    a: "Onze leerlingen slagen in 64% van de gevallen in één keer — dat ligt boven het landelijk gemiddelde. We trainen specifiek op wat jij lastig vindt zodat je écht klaar bent voor je examen.",
  },
  {
    q: "Hoe plan ik mijn gratis proefles in?",
    a: "Heel simpel: stuur ons een berichtje via WhatsApp (06-20933773) of vul het contactformulier in. We nemen dezelfde dag nog contact op om een tijd te plannen die jou uitkomt — vaak al binnen een week.",
  },
];

const FAQItem = ({ q, a, idx, openIdx, setOpenIdx }) => {
  const open = openIdx === idx;
  return (
    <div
      className="card overflow-hidden"
      data-testid={`faq-item-${idx}`}
    >
      <button
        type="button"
        onClick={() => setOpenIdx(open ? -1 : idx)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-white/5 transition-colors"
        data-testid={`faq-question-${idx}`}
      >
        <span className="text-white text-base md:text-lg font-medium tracking-tight">
          {q}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="flex-shrink-0 w-8 h-8 rounded-full border border-gold/40 flex items-center justify-center text-gold"
        >
          <CaretDown size={14} weight="bold" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-silver leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = ({ hideHeading = false }) => {
  const [openIdx, setOpenIdx] = useState(0);
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: i * 0.05 },
    }),
  };

  return (
    <section
      id="faq"
      className="relative py-24 lg:py-32 px-6 md:px-12"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto">
        {!hideHeading && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-12 max-w-2xl"
          >
            <div className="overline mb-4 flex items-center gap-2">
              <Question size={14} weight="duotone" /> Veelgestelde vragen
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
              Antwoorden op{" "}
              <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
                jouw vragen.
              </span>
            </h2>
            <p className="text-silver mt-6 leading-relaxed">
              Niet gevonden wat je zocht? Stuur ons een WhatsApp — we helpen je
              graag verder.
            </p>
          </motion.div>
        )}

        <div className="space-y-3" data-testid="faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              variants={fadeUp}
            >
              <FAQItem
                q={item.q}
                a={item.a}
                idx={i}
                openIdx={openIdx}
                setOpenIdx={setOpenIdx}
              />
            </motion.div>
          ))}
        </div>

        <div className="mt-16 card-highlight p-8 text-center">
          <h3 className="text-2xl text-white font-light mb-3">
            Heb je nog een andere vraag?
          </h3>
          <p className="text-silver mb-6">
            Onze AI-assistent (linksonder) of WhatsApp helpt je direct verder.
          </p>
          <a
            href={whatsappLink("Hoi! Ik heb een vraag.")}
            target="_blank"
            rel="noreferrer"
            className="btn-gold inline-flex"
            data-testid="faq-whatsapp-cta"
          >
            <WhatsappLogo size={18} weight="bold" /> Stel je vraag op WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

// ----------------- WHATSAPP CONTACT MODAL -----------------
const WhatsappContactModal = ({ open, onClose, packageName, context }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    street: "",
    house_number: "",
    postcode: "",
    city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sentLink, setSentLink] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        phone: "",
        street: "",
        house_number: "",
        postcode: "",
        city: "",
        message: "",
      });
      setSentLink("");
    }
  }, [open, packageName]);

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { ...form, package: packageName || "" };
    const waUrl = whatsappLink(
      buildWhatsAppSummary(payload, context || "Pakket interesse")
    );
    setSentLink(waUrl);

    // Open WhatsApp synchronously
    window.open(waUrl, "_blank", "noopener,noreferrer");

    // Request reCAPTCHA v3 token in background, then save to DB
    getCaptchaToken("package_inquiry").then((token) => {
      axios
        .post(`${API}/contact`, { ...payload, recaptcha_token: token })
        .catch((err) => console.error("Modal contact save failed:", err));
    });

    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          data-testid="wa-modal"
        >
          <div
            className="absolute inset-0 bg-[#070a13]/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="wa-modal-overlay"
          ></div>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-lg card-highlight p-8 max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-silver hover:text-gold transition-colors"
              aria-label="Sluiten"
              data-testid="wa-modal-close"
            >
              <X size={22} />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-[#25D366] rounded-full flex items-center justify-center">
                <WhatsappLogo size={22} weight="fill" color="#fff" />
              </div>
              <div>
                <div className="overline">Via WhatsApp</div>
                <h3 className="text-xl text-white tracking-tight">
                  {packageName
                    ? `Vraag over ${packageName}`
                    : "Neem contact op"}
                </h3>
              </div>
            </div>

            {sentLink ? (
              <div className="py-6 text-center">
                <CheckCircle
                  size={48}
                  weight="duotone"
                  className="text-gold mx-auto mb-4"
                />
                <h4 className="text-xl text-white mb-2">Bijna klaar!</h4>
                <p className="text-silver mb-6">
                  Als WhatsApp niet vanzelf opent, klik dan hieronder.
                </p>
                <a
                  href={sentLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold inline-flex justify-center"
                  data-testid="wa-modal-open-btn"
                >
                  <WhatsappLogo size={18} weight="fill" /> Open WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="wa-modal-form">
                <p className="text-silver text-sm">
                  Vul je gegevens in — we krijgen direct een samenvatting op
                  WhatsApp en nemen snel contact op.
                </p>
                <div>
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Naam
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="Jouw naam"
                    data-testid="wa-modal-input-name"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      className="field"
                      placeholder="je@email.nl"
                      data-testid="wa-modal-input-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                      Telefoon
                    </label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      required
                      className="field"
                      placeholder="06-..."
                      data-testid="wa-modal-input-phone"
                    />
                  </div>
                </div>

                {/* Adresvelden */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                      Straat
                    </label>
                    <input
                      name="street"
                      value={form.street}
                      onChange={onChange}
                      required
                      className="field"
                      placeholder="Bijv. Stadhuisplein"
                      data-testid="wa-modal-input-street"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                      Huisnr.
                    </label>
                    <input
                      name="house_number"
                      value={form.house_number}
                      onChange={onChange}
                      required
                      className="field"
                      placeholder="12 A"
                      data-testid="wa-modal-input-housenumber"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                      Postcode
                    </label>
                    <input
                      name="postcode"
                      value={form.postcode}
                      onChange={onChange}
                      required
                      className="field"
                      placeholder="5611 EM"
                      data-testid="wa-modal-input-postcode"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                      Plaats
                    </label>
                    <input
                      name="city"
                      value={form.city}
                      onChange={onChange}
                      required
                      className="field"
                      placeholder="Eindhoven"
                      data-testid="wa-modal-input-city"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Vraag / bericht
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={3}
                    className="field resize-none"
                    placeholder={
                      packageName
                        ? `Wat wil je weten over ${packageName}?`
                        : "Stel je vraag..."
                    }
                    data-testid="wa-modal-input-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full justify-center"
                  data-testid="wa-modal-submit-btn"
                >
                  {submitting ? "Versturen..." : "Verstuur via WhatsApp"}{" "}
                  {!submitting && <WhatsappLogo size={18} weight="bold" />}
                </button>
                <p className="text-xs text-silver/70 text-center">
                  We openen WhatsApp met je samenvatting. Beveiligd met reCAPTCHA.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ----------------- PACKAGES -----------------
const Packages = () => {
  const [modalPkg, setModalPkg] = useState(null);

  const packages = [
    {
      id: "economy",
      name: "Economy Class",
      price: "€1.800",
      priceValue: 1800,
      tagline: "Voor de snelle leerling",
      lessons: "25 rijlessen van 60 minuten",
      highlight: false,
      aura: "#4f8dff",
      icon: <PilotCarIcon size={36} weight="duotone" />,
    },
    {
      id: "business",
      name: "Business Class",
      price: "€2.400",
      priceValue: 2400,
      tagline: "Comfort en zelfvertrouwen",
      lessons: "35 rijlessen van 60 minuten",
      highlight: false,
      aura: "#b8bfca",
      icon: <PilotCarIcon size={36} weight="duotone" />,
    },
    {
      id: "first",
      name: "First Class",
      price: "€3.000",
      priceValue: 3000,
      tagline: "Maximaal voorbereid",
      lessons: "45 rijlessen van 60 minuten",
      highlight: true,
      aura: "#a78bfa",
      icon: <PilotCarIcon size={36} weight="fill" />,
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.12 },
    }),
  };

  return (
    <section
      id="pakketten"
      className="relative py-24 lg:py-32 px-6 md:px-12 bg-secondary"
      data-testid="packages-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16 max-w-2xl"
        >
          <div className="overline mb-4">Pakketten</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
            Ga je voor economy, business
            <br />
            of juist{" "}
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">first class</span>?
          </h2>
          <p className="text-silver mt-6 leading-relaxed">
            Alle pakketten zijn inclusief gratis proefles, praktijkexamen en
            1 gratis herexamen.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
            >
              <Tilt3D max={6} scale={1.015} className="h-full">
                <div
                  className={`p-8 lg:p-10 flex flex-col h-full pkg-aura ${
                    pkg.highlight ? "pkg-aura-on gold-tracer" : ""
                  }`}
                  style={{ "--aura": pkg.aura, transformStyle: "preserve-3d" }}
                  data-testid={`package-card-${pkg.id}`}
                >
              {pkg.highlight && (
                <div className="overline mb-4 flex items-center gap-2">
                  <Star size={12} weight="fill" className="anim-twinkle" /> Meest gekozen
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <span className="text-gold anim-salute">{pkg.icon}</span>
                <span className="text-xs text-silver tracking-wider uppercase">
                  {pkg.tagline}
                </span>
              </div>

              <h3 className="text-2xl md:text-3xl text-white tracking-tight mb-2">
                {pkg.name}
              </h3>
              <div className="text-5xl font-extralight text-white mb-1">
                <CountUp to={pkg.priceValue} duration={1600} />
                <span className="text-base text-silver">,-</span>
              </div>
              <div className="text-sm text-silver mb-8">{pkg.lessons}</div>

              <ul className="space-y-3 mb-10 text-sm">
                <li className="flex items-start gap-2 text-silver">
                  <CheckCircle
                    size={18}
                    weight="duotone"
                    className="text-gold flex-shrink-0 mt-0.5"
                  />
                  Gratis proefles
                </li>
                <li className="flex items-start gap-2 text-silver">
                  <CheckCircle
                    size={18}
                    weight="duotone"
                    className="text-gold flex-shrink-0 mt-0.5"
                  />
                  Praktijkexamen inbegrepen
                </li>
                <li className="flex items-start gap-2 text-silver">
                  <CheckCircle
                    size={18}
                    weight="duotone"
                    className="text-gold flex-shrink-0 mt-0.5"
                  />
                  1 gratis herexamen
                </li>
                <li className="flex items-start gap-2 text-silver">
                  <CheckCircle
                    size={18}
                    weight="duotone"
                    className="text-gold flex-shrink-0 mt-0.5"
                  />
                  Ophaalservice in de regio
                </li>
              </ul>

              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => setModalPkg(pkg)}
                  className={`w-full justify-center ${
                    pkg.highlight ? "btn-gold" : "btn-ghost"
                  }`}
                  data-testid={`package-whatsapp-btn-${pkg.id}`}
                >
                  <WhatsappLogo size={18} weight="bold" /> Vraag aan via WhatsApp
                </button>
              </div>
                </div>
              </Tilt3D>
            </motion.div>
          ))}
        </div>
      </div>

      <WhatsappContactModal
        open={!!modalPkg}
        onClose={() => setModalPkg(null)}
        packageName={modalPkg?.name || ""}
        context={modalPkg ? `Interesse in ${modalPkg.name} (${modalPkg.price})` : ""}
      />
    </section>
  );
};

// ----------------- TESTIMONIALS -----------------
const Testimonials = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.1 },
    }),
  };

  return (
    <section
      id="reviews"
      className="relative py-24 lg:py-32 px-6 md:px-12"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16 max-w-2xl"
        >
          <div className="overline mb-4">Leerlingen</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
            Van leerling naar
            <br />
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
              trotse bestuurder.
            </span>
          </h2>
          <p className="text-silver mt-6 leading-relaxed">
            Een greep uit onze geslaagde leerlingen. Jij de volgende?
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              variants={fadeUp}
            >
              <Tilt3D max={5} scale={1.02} className="h-full">
                <figure
                  className="card vignette-inner p-3 flex flex-col group h-full"
                  style={{ transformStyle: "preserve-3d" }}
                  data-testid={`testimonial-card-${t.id}`}
                >
              <div className="relative overflow-hidden">
                <img
                  src={t.image}
                  alt={`Geslaagde leerling ${t.label}`}
                  className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <figcaption className="px-3 pt-5 pb-4 flex-1 flex flex-col">
                <motion.div
                  className="flex items-center gap-2 mb-3"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 + i * 0.08 } },
                  }}
                >
                  {[0, 1, 2, 3, 4].map((s) => (
                    <motion.span
                      key={s}
                      variants={{
                        hidden: { opacity: 0, scale: 0, rotate: -180 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          rotate: 0,
                          transition: {
                            type: "spring",
                            stiffness: 380,
                            damping: 14,
                          },
                        },
                      }}
                      className="inline-block"
                    >
                      <Star size={14} weight="fill" className="text-gold" />
                    </motion.span>
                  ))}
                  <span className="overline ml-2">{t.label}</span>
                </motion.div>
                <blockquote className="text-silver-light leading-relaxed italic">
                  “{t.quote}”
                </blockquote>
              </figcaption>
                </figure>
              </Tilt3D>
            </motion.div>
          ))}

          {/* Call-to-action tile */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            custom={TESTIMONIALS.length}
            variants={fadeUp}
            className="card-highlight p-8 lg:p-10 flex flex-col justify-center items-start"
            data-testid="testimonial-cta-card"
          >
            <div className="overline mb-4">Word jij de volgende?</div>
            <h3 className="text-3xl text-white font-light tracking-tight mb-4 leading-tight">
              Jouw foto hier.<br />
              <span className="text-gold italic">In één keer geslaagd.</span>
            </h3>
            <p className="text-silver mb-8 leading-relaxed">
              Start met een gratis proefles en ontdek waarom onze leerlingen
              lachend het examen uitrijden.
            </p>
            <Link
              to="/contact"
              className="btn-gold"
              data-testid="testimonial-cta-btn"
            >
              Plan proefles <ArrowRight size={16} weight="bold" className="anim-arrow" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ----------------- ABOUT -----------------
const About = () => (
  <section
    id="over"
    className="relative py-24 lg:py-32 px-6 md:px-12"
    data-testid="about-section"
  >
    <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="lg:col-span-5 relative"
      >
        <div className="relative">
          <div className="absolute -top-6 -left-6 w-40 h-40 border border-gold/40 -z-0"></div>
          <div className="relative z-10 card-highlight overflow-hidden">
            <img
              src="/seasons/autumn.png"
              alt="Rijschool AutoPilot in actie"
              className="w-full aspect-[4/5] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f2b] via-[#1a1f2b]/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={LOGO_URL}
                  alt="Logo"
                  className="w-12 h-12 rounded-full border border-gold/50 object-cover"
                />
                <div>
                  <div className="text-white font-semibold">Samet Dersuneli</div>
                  <div className="text-xs text-gold uppercase tracking-[0.2em]">
                    Eigenaar & Instructeur
                  </div>
                </div>
              </div>
              <p className="text-silver-light italic text-sm leading-relaxed">
                “Ik zie graag hoe leerlingen van onzeker naar zelfverzekerd
                rijden. Dat is waar ik het voor doe.”
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="lg:col-span-7"
      >
        <div className="overline mb-4">Over ons</div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white mb-6">
          Naar een hoger niveau met
          <br />
          <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
            professionele begeleiding.
          </span>
        </h2>
        <p className="text-silver leading-relaxed mb-6 text-lg">
          Bij AutoPilot krijg je rijles op een manier die bij jou past.
          Persoonlijk, rustig en zonder stress. Je leert niet alleen om te
          slagen voor je examen, maar ook om zelfverzekerd en veilig de weg
          op te gaan.
        </p>
        <p className="text-silver leading-relaxed mb-10">
          Met een professionele aanpak, een ontspannen sfeer en duidelijke
          uitleg haal jij het beste uit elke rijles. Start bij AutoPilot en
          ervaar dat rijles ook leuk, leerzaam en effectief kan zijn.
        </p>

        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="border-l-2 border-gold pl-4">
            <div className="text-3xl font-light text-white">
              <CountUp to={100} prefix="" suffix="+" />
            </div>
            <div className="text-xs text-silver mt-1 uppercase tracking-wider">
              Geslaagde leerlingen
            </div>
          </div>
          <div className="border-l-2 border-gold pl-4">
            <div className="text-3xl font-light text-white">
              <CountUp to={3} prefix="" suffix="+" duration={900} />
            </div>
            <div className="text-xs text-silver mt-1 uppercase tracking-wider">
              Jaar ervaring
            </div>
          </div>
          <div className="border-l-2 border-gold pl-4">
            <div className="text-3xl font-light text-white">
              <CountUp to={64} prefix="" suffix="%" duration={1400} />
            </div>
            <div className="text-xs text-silver mt-1 uppercase tracking-wider">
              Slagingspercentage
            </div>
          </div>
        </div>

        <Link
          to="/contact"
          className="btn-gold"
          data-testid="about-cta-btn"
        >
          Plan je proefles <ArrowRight size={16} weight="bold" className="anim-arrow" />
        </Link>
      </motion.div>
    </div>
  </section>
);

// ----------------- CONTACT -----------------
const buildWhatsAppSummary = (form, context = "Gratis proefles") => {
  const lines = [
    `*Nieuwe aanvraag via website* — ${context}`,
    "",
    `*Naam:* ${form.name || "-"}`,
    `*Email:* ${form.email || "-"}`,
    `*Telefoon:* ${form.phone || "-"}`,
  ];
  // Bouw een geformatteerde adresregel (alleen indien ingevuld)
  const streetLine = [form.street, form.house_number].filter(Boolean).join(" ").trim();
  const cityLine = [form.postcode, form.city].filter(Boolean).join(" ").trim();
  if (streetLine) lines.push(`*Adres:* ${streetLine}`);
  if (cityLine) lines.push(`*Postcode/Plaats:* ${cityLine}`);
  if (form.package) lines.push(`*Pakket:* ${form.package}`);
  if (form.message) lines.push("", `*Bericht:*`, form.message);
  lines.push("", "— Verzonden via rijschool-autopilot.nl");
  return lines.join("\n");
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    street: "",
    house_number: "",
    postcode: "",
    city: "",
    package: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waLink, setWaLink] = useState("");
  const [error, setError] = useState("");

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const payload = { ...form };
    const waUrl = whatsappLink(buildWhatsAppSummary(payload));
    setWaLink(waUrl);

    // Open WhatsApp synchronously (during the click gesture, never blocked)
    window.open(waUrl, "_blank", "noopener,noreferrer");

    // Request reCAPTCHA v3 token in background, then POST to backend
    getCaptchaToken("contact_submit").then((token) => {
      axios
        .post(`${API}/contact`, { ...payload, recaptcha_token: token })
        .catch((err) => console.error("Contact save failed:", err));
    });

    setSubmitting(false);
    setSubmitted(true);
    setForm({
      name: "",
      email: "",
      phone: "",
      street: "",
      house_number: "",
      postcode: "",
      city: "",
      package: "",
      message: "",
    });
  };

  return (
    <section
      id="contact"
      className="relative py-24 lg:py-32 px-6 md:px-12 bg-secondary"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="overline mb-4">Contact</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white mb-6">
            Klaar voor{" "}
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">take-off</span>?
          </h2>
          <p className="text-silver leading-relaxed mb-10 text-lg">
            Proefles, pakket of andere vraag? Vul het formulier in — het wordt
            automatisch naar ons WhatsApp gestuurd. Of stuur direct een bericht.
          </p>

          <div className="space-y-6">
            <a
              href={whatsappLink("Hoi! Ik heb een vraag over rijlessen.")}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 group"
              data-testid="contact-whatsapp-link"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-gold/40 group-hover:bg-gold/10 transition-colors">
                <WhatsappLogo size={22} weight="duotone" className="text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-silver">
                  WhatsApp
                </div>
                <div className="text-white group-hover:text-gold transition-colors">
                  06-20933773
                </div>
              </div>
            </a>

            <a
              href="https://instagram.com/rijschoolautopilot"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 group"
              data-testid="contact-instagram-link"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-gold/40 group-hover:bg-gold/10 transition-colors">
                <InstagramLogo size={22} weight="duotone" className="text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-silver">
                  Instagram
                </div>
                <div className="text-white group-hover:text-gold transition-colors">
                  @rijschoolautopilot
                </div>
              </div>
            </a>

            <a
              href="https://tiktok.com/@autopilot-sd"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 group"
              data-testid="contact-tiktok-link"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-gold/40 group-hover:bg-gold/10 transition-colors">
                <TiktokLogo size={22} weight="duotone" className="text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-silver">
                  TikTok
                </div>
                <div className="text-white group-hover:text-gold transition-colors">
                  Autopilot-sd
                </div>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={onSubmit}
          className="card p-8 lg:p-10 space-y-5"
          data-testid="contact-form"
        >
          {submitted ? (
            <div
              className="text-center py-10"
              data-testid="contact-success-message"
            >
              <CheckCircle
                size={56}
                weight="duotone"
                className="text-gold mx-auto mb-4"
              />
              <h3 className="text-2xl text-white mb-2">Verzonden!</h3>
              <p className="text-silver mb-6">
                Als WhatsApp niet vanzelf opent, klik dan op de knop hieronder
                om je bericht te versturen.
              </p>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="btn-gold justify-center inline-flex"
                data-testid="contact-open-whatsapp-btn"
              >
                <WhatsappLogo size={18} weight="fill" /> Open WhatsApp
              </a>
              <div>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setWaLink("");
                  }}
                  className="mt-6 btn-ghost"
                  data-testid="contact-new-message-btn"
                >
                  Nieuw bericht
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                  Naam
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="field"
                  placeholder="Jouw naam"
                  data-testid="contact-input-name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="je@email.nl"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Telefoon
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="06-..."
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>

              {/* Adresvelden */}
              <div className="grid grid-cols-3 gap-5">
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Straat
                  </label>
                  <input
                    name="street"
                    value={form.street}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="Bijv. Stadhuisplein"
                    data-testid="contact-input-street"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Huisnr.
                  </label>
                  <input
                    name="house_number"
                    value={form.house_number}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="12 A"
                    data-testid="contact-input-housenumber"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Postcode
                  </label>
                  <input
                    name="postcode"
                    value={form.postcode}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="5611 EM"
                    data-testid="contact-input-postcode"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                    Plaats
                  </label>
                  <input
                    name="city"
                    value={form.city}
                    onChange={onChange}
                    required
                    className="field"
                    placeholder="Eindhoven"
                    data-testid="contact-input-city"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                  Pakket (optioneel)
                </label>
                <select
                  name="package"
                  value={form.package}
                  onChange={onChange}
                  className="field"
                  data-testid="contact-select-package"
                >
                  <option value="">Ik wil eerst een proefles</option>
                  <option value="Economy Class">Economy Class – €1.800</option>
                  <option value="Business Class">
                    Business Class – €2.400
                  </option>
                  <option value="First Class">First Class – €3.000</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-silver mb-2 block">
                  Bericht (optioneel)
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  className="field resize-none"
                  placeholder="Vertel ons wat je nodig hebt..."
                  data-testid="contact-input-message"
                />
              </div>

              {error && (
                <div
                  className="p-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
                  data-testid="contact-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full justify-center"
                data-testid="contact-submit-btn"
              >
                {submitting
                  ? "Versturen..."
                  : "Verstuur via WhatsApp"}{" "}
                {!submitting && <WhatsappLogo size={18} weight="bold" />}
              </button>
              <p className="text-[10px] text-silver/50 text-center leading-snug">
                Beveiligd met reCAPTCHA · Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-gold underline-offset-2 hover:underline"
                >
                  Privacy
                </a>{" "}
                &{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-gold underline-offset-2 hover:underline"
                >
                  Voorwaarden
                </a>
              </p>
              <p className="text-xs text-muted text-center text-silver/70">
                We openen WhatsApp met je gegevens ingevuld.
              </p>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
};

// ----------------- FOOTER -----------------
const Footer = () => {
  const location = useLocation();
  const lang = location.pathname.startsWith("/tr")
    ? "tr"
    : location.pathname.startsWith("/en")
    ? "en"
    : "nl";
  const t = {
    nl: {
      tagline:
        "Premium rijlessen in Eindhoven en Helmond. Persoonlijk, rustig en effectief.",
      nav: "Navigatie",
      contact: "Contact",
    },
    tr: {
      tagline:
        "Eindhoven ve Helmond'da premium direksiyon dersleri. Kişisel, sakin ve etkili.",
      nav: "Gezinme",
      contact: "İletişim",
    },
    en: {
      tagline:
        "Premium driving lessons in Eindhoven and Helmond. Personal, calm, and effective.",
      nav: "Navigation",
      contact: "Contact",
    },
  }[lang];
  return (
  <footer
    className="relative py-20 px-6 md:px-12 border-t border-white/10"
    data-testid="site-footer"
  >
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-3 gap-12 mb-16">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img
              src={LOGO_URL}
              alt="Rijschool AutoPilot"
              className="h-14 w-14 object-cover rounded-full border border-[rgba(212,175,55,0.4)]"
            />
            <div>
              <div className="text-white font-semibold">Rijschool AutoPilot</div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">
                Drive like a pilot
              </div>
            </div>
          </div>
          <p className="text-silver text-sm leading-relaxed max-w-sm">
            {t.tagline}
          </p>
        </div>

        <div>
          <div className="overline mb-4">{t.nav}</div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link
                to="/waarom"
                className="text-silver hover:text-gold transition-colors"
              >
                Waarom wij
              </Link>
            </li>
            <li>
              <Link
                to="/pakketten"
                className="text-silver hover:text-gold transition-colors"
              >
                Pakketten
              </Link>
            </li>
            <li>
              <Link
                to="/reviews"
                className="text-silver hover:text-gold transition-colors"
              >
                Reviews
              </Link>
            </li>
            <li>
              <Link
                to="/over-ons"
                className="text-silver hover:text-gold transition-colors"
              >
                Over ons
              </Link>
            </li>
            <li>
              <Link
                to="/faq"
                className="text-silver hover:text-gold transition-colors"
              >
                FAQ
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className="text-silver hover:text-gold transition-colors"
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4">{t.contact}</div>
          <ul className="space-y-2 text-sm">
            <li className="text-silver">06-20933773 (WhatsApp)</li>
            <li className="text-silver">Eindhoven & Helmond</li>
          </ul>
          <div className="flex items-center gap-3 mt-5">
            <a
              href={whatsappLink("Hoi!")}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/10 hover:border-gold flex items-center justify-center transition-colors anim-social"
              data-testid="footer-whatsapp"
              aria-label="WhatsApp"
            >
              <WhatsappLogo size={18} className="text-gold" weight="duotone" />
            </a>
            <a
              href="https://instagram.com/rijschoolautopilot"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/10 hover:border-gold flex items-center justify-center transition-colors anim-social"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <InstagramLogo size={18} className="text-gold" weight="duotone" />
            </a>
            <a
              href="https://tiktok.com/@autopilot-sd"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/10 hover:border-gold flex items-center justify-center transition-colors anim-social"
              data-testid="footer-tiktok"
              aria-label="TikTok"
            >
              <TiktokLogo size={18} className="text-gold" weight="duotone" />
            </a>
          </div>
        </div>
      </div>

      <div className="divider-line mb-8"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-silver">
        <div>
          © {new Date().getFullYear()} Rijschool AutoPilot · Alle rechten voorbehouden
        </div>
        <div className="text-muted">Drive slim. Drive veilig. Drive op Autopilot.</div>
      </div>
    </div>
  </footer>
  );
};

// ----------------- FLOATING WHATSAPP BUTTON -----------------
const FloatingWhatsapp = () => (
  <a
    href={whatsappLink("Hoi! Ik heb een vraag over rijlessen.")}
    target="_blank"
    rel="noreferrer"
    className="fixed bottom-6 right-6 z-30 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform pulse-dot"
    aria-label="Open WhatsApp"
    data-testid="floating-whatsapp-btn"
  >
    <WhatsappLogo size={28} weight="fill" color="#ffffff" className="anim-wa" />
  </a>
);

// ----------------- PAGE BANNER (used on sub-pages) -----------------
const PageBanner = ({ overline, title, subtitle, accent }) => (
  <section
    className="relative pt-40 pb-16 px-6 md:px-12 overflow-hidden noise"
    data-testid="page-banner"
  >
    <div className="absolute inset-0 -z-0">
      <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[rgba(212,175,55,0.05)] blur-3xl"></div>
    </div>
    <div className="relative max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <span className="runway-stripes w-16 block"></span>
        <span className="overline">{overline}</span>
      </div>
      <h1
        className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.05]"
        data-testid="page-banner-title"
      >
        {title}{" "}
        {accent && (
          <span className="font-display-italic text-gold text-6xl md:text-7xl lg:text-8xl">{accent}</span>
        )}
      </h1>
      {subtitle && (
        <p className="text-lg text-silver mt-6 max-w-2xl leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  </section>
);

// ----------------- SLAAGDEN MARQUEE -----------------
const SLAAGDEN_NAMES = [
  "Mike",
  "Sara",
  "Jan",
  "Emma",
  "Lars",
  "Yara",
  "Tijn",
  "Femke",
  "Daan",
  "Lotte",
  "Sem",
  "Eva",
];

const SlaagdenMarquee = () => (
  <div
    className="relative w-full overflow-hidden border-y border-white/10"
    style={{ background: "rgba(0,0,0,0.18)" }}
    data-testid="slaagden-marquee"
  >
    <div className="flex whitespace-nowrap py-3 marquee-track">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex shrink-0 items-center">
          {SLAAGDEN_NAMES.map((name, i) => (
            <span
              key={`${dup}-${i}`}
              className="flex items-center gap-3 px-8 text-sm tracking-wide text-silver"
            >
              <span className="text-gold">✓</span>
              <span className="text-white">{name}</span>
              <span className="text-silver/60 text-xs uppercase">
                geslaagd
              </span>
              <span className="text-silver/30 mx-1">·</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// ----------------- PAGE TRANSITION WRAPPER -----------------
const PageFade = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

// ----------------- LAYOUT WRAPPER -----------------
const SiteLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
    <FloatingWhatsapp />
    <ChatWidget />
    <LiveActivityToast />
  </>
);

// ----------------- SPOED CALLOUT STRIP -----------------
const SpoedCallout = () => (
  <section
    className="relative px-6 md:px-12 py-6"
    data-testid="spoed-callout"
  >
    <div className="max-w-7xl mx-auto">
      <Link
        to="/spoed"
        className="group relative block overflow-hidden gold-tracer card-highlight scan-line px-6 sm:px-10 py-6 sm:py-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8"
        data-testid="spoed-callout-link"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div
            className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 grid place-items-center rounded-full"
            style={{
              background: "rgba(224,192,104,0.12)",
              border: "1px solid rgba(224,192,104,0.5)",
              boxShadow: "0 0 22px rgba(224,192,104,0.18)",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gold">
              <path
                d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"
                fill="currentColor"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth="1.2"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1 flex items-center gap-2">Spoedopleiding</div>
            <div className="text-white text-lg sm:text-xl tracking-tight leading-snug">
              Haast met je rijbewijs?{" "}
              <span className="font-display-italic text-gold text-xl sm:text-2xl">
                Bereken jouw spoedpakket
              </span>{" "}
              <span className="text-silver text-sm sm:text-base block sm:inline mt-1 sm:mt-0">
                — schuif aan de balken en zie direct een indicatieprijs.
              </span>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0">
          <span className="btn-ghost group-hover:bg-[rgba(224,192,104,0.12)] pointer-events-none">
            Naar de calculator
            <ArrowRight size={16} weight="bold" className="anim-arrow" />
          </span>
        </div>
      </Link>
    </div>
  </section>
);

// ----------------- HOME -----------------
const Home = () => (
  <SiteLayout>
    <Hero />
    <SlaagdenMarquee />
    <Features />
    <RunwayDivider />
    <Packages />
    <SpoedCallout />
    <RunwayDivider />
    <Testimonials />
    <RunwayDivider />
    <About />
    <RunwayDivider />
    <FAQ />
    <TakeoffCTA />
    <Contact />
  </SiteLayout>
);

// ----------------- SUB-PAGES -----------------
const WaaromPage = () => (
  <SiteLayout>
    <PageBanner
      overline="Waarom AutoPilot"
      title="Rij slim."
      accent="Rij op Autopilot."
      subtitle="Vijf redenen waarom leerlingen voor AutoPilot kiezen — en waarom ze lachend slagen."
    />
    <Features />
  </SiteLayout>
);

const PakkettenPage = () => (
  <SiteLayout>
    <PageBanner
      overline="Pakketten"
      title="Economy, Business"
      accent="of First Class?"
      subtitle="Transparante prijzen, gratis proefles en 1 gratis herexamen inbegrepen bij elk pakket."
    />
    <Packages />
  </SiteLayout>
);

const ReviewsPage = () => (
  <SiteLayout>
    <PageBanner
      overline="Leerlingen"
      title="Van leerling naar"
      accent="trotse bestuurder."
      subtitle="Een selectie van onze geslaagde leerlingen. Jij de volgende?"
    />
    <Testimonials />
  </SiteLayout>
);

const OverOnsPage = () => (
  <SiteLayout>
    <PageBanner
      overline="Over ons"
      title="Professionele"
      accent="rijbegeleiding."
      subtitle="Leer rijden op een manier die bij jou past — persoonlijk, rustig en zonder stress."
    />
    <About />
  </SiteLayout>
);

const ContactPage = () => (
  <SiteLayout>
    <PageBanner
      overline="Contact"
      title="Klaar voor"
      accent="take-off?"
      subtitle="Proefles, pakket of andere vraag? Laat van je horen — we nemen snel contact op."
    />
    <Contact />
  </SiteLayout>
);

const FAQPage = () => (
  <SiteLayout>
    <PageBanner
      overline="FAQ"
      title="Veelgestelde"
      accent="vragen."
      subtitle="De antwoorden op de meest gestelde vragen over rijlessen, examen en onze pakketten."
    />
    <FAQ hideHeading />
  </SiteLayout>
);

// ----------------- APP -----------------
const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageFade>
              <Home />
            </PageFade>
          }
        />
        <Route
          path="/waarom"
          element={
            <PageFade>
              <WaaromPage />
            </PageFade>
          }
        />
        <Route
          path="/pakketten"
          element={
            <PageFade>
              <PakkettenPage />
            </PageFade>
          }
        />
        <Route
          path="/reviews"
          element={
            <PageFade>
              <ReviewsPage />
            </PageFade>
          }
        />
        <Route
          path="/over-ons"
          element={
            <PageFade>
              <OverOnsPage />
            </PageFade>
          }
        />
        <Route
          path="/faq"
          element={
            <PageFade>
              <FAQPage />
            </PageFade>
          }
        />
        <Route
          path="/contact"
          element={
            <PageFade>
              <ContactPage />
            </PageFade>
          }
        />
        <Route
          path="/payment-success"
          element={
            <PageFade>
              <PaymentSuccess />
            </PageFade>
          }
        />
        <Route
          path="/tr"
          element={
            <PageFade>
              <SiteLayout>
                <TurkishLanding />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/en"
          element={
            <PageFade>
              <SiteLayout>
                <EnglishLanding />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/spoed"
          element={
            <PageFade>
              <SiteLayout>
                <SpoedPage lang="nl" />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/tarieven"
          element={
            <PageFade>
              <SiteLayout>
                <TarievenPage />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/leskaart"
          element={
            <PageFade>
              <SiteLayout>
                <LeskaartPage />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/blog"
          element={
            <PageFade>
              <SiteLayout>
                <BlogIndex />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <PageFade>
              <SiteLayout>
                <BlogDetail />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/tr/spoed"
          element={
            <PageFade>
              <SiteLayout>
                <SpoedPage lang="tr" />
              </SiteLayout>
            </PageFade>
          }
        />
        <Route
          path="/en/spoed"
          element={
            <PageFade>
              <SiteLayout>
                <SpoedPage lang="en" />
              </SiteLayout>
            </PageFade>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <LoadingScreen />
        <CursorTracker />
        <AnimatedRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
